/**
 * Collection Subfilter - Client-side filtering
 * Filters products in the grid based on collection membership
 * without page reload, with fade animations and URL updates
 */

class CollectionFilter {
  constructor() {
    this.filterLinks = document.querySelectorAll('[data-collection-filter]');
    this.productGrid = document.getElementById('product-grid');
    this.productItems = null;
    this.currentFilter = null;
    this.animationDuration = 300; // ms

    if (!this.filterLinks.length || !this.productGrid) return;

    this.productItems = this.productGrid.querySelectorAll('.grid__item[data-collections]');
    // Get ticker and promo elements that should hide when filtering
    this.promoElements = this.productGrid.querySelectorAll('.ticker-promo-bar, .collection-promo-banner');
    // Product count elements
    this.productCountDesktop = document.getElementById('ProductCountDesktop');
    this.productCountMobile = document.getElementById('ProductCount');
    // Store original count for "Shop All"
    this.originalCount = this.productItems.length;

    this.init();
  }

  init() {
    // Check URL for initial filter state
    this.checkUrlForFilter();

    // Bind click events
    this.filterLinks.forEach(link => {
      link.addEventListener('click', (e) => this.handleFilterClick(e, link));
    });

    // Listen for facets update (sorting/filtering) to re-apply subfilter
    document.addEventListener('facets:updated', () => this.onFacetsUpdated());
  }

  onFacetsUpdated() {
    // Re-acquire DOM references after grid was replaced
    this.productGrid = document.getElementById('product-grid');
    if (!this.productGrid) return;

    this.productItems = this.productGrid.querySelectorAll('.grid__item[data-collections]');
    this.promoElements = this.productGrid.querySelectorAll('.ticker-promo-bar, .collection-promo-banner');
    this.productCountDesktop = document.getElementById('ProductCountDesktop');
    this.productCountMobile = document.getElementById('ProductCount');
    this.originalCount = this.productItems.length;

    // Re-apply current filter if one is active
    if (this.currentFilter) {
      this.applyFilter(this.currentFilter, false);
    }
  }

  checkUrlForFilter() {
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');

    if (filterParam) {
      this.currentFilter = filterParam;
      this.applyFilter(filterParam, false);
      this.updateActiveState(filterParam);
    }
  }

  handleFilterClick(e, link) {
    e.preventDefault();

    const filterHandle = link.dataset.collectionFilter;
    const filterType = link.dataset.filterType;

    // If clicking the already active filter, do nothing
    if (this.currentFilter === filterHandle && filterType !== 'shop-all') {
      return;
    }

    // Update active state
    this.updateActiveState(filterHandle);

    // Apply filter
    if (filterType === 'shop-all' || filterHandle === '') {
      this.showAll();
      this.updateUrl('');
      this.currentFilter = null;
    } else {
      this.applyFilter(filterHandle, true);
      this.updateUrl(filterHandle);
      this.currentFilter = filterHandle;
    }
  }

  updateActiveState(filterHandle) {
    this.filterLinks.forEach(link => {
      const parent = link.closest('.collection-subfilter') || link;
      if (link.dataset.collectionFilter === filterHandle) {
        link.classList.add('collection-subfilter--active');
      } else {
        link.classList.remove('collection-subfilter--active');
      }
    });
  }

  applyFilter(collectionHandle, animate = true) {
    if (!this.productItems) return;

    const itemsToHide = [];
    const itemsToShow = [];

    this.productItems.forEach(item => {
      const collections = item.dataset.collections ? item.dataset.collections.split(',') : [];
      const matchesFilter = collections.includes(collectionHandle);

      if (matchesFilter) {
        itemsToShow.push(item);
      } else {
        itemsToHide.push(item);
      }
    });

    // Hide promo elements when filtering
    this.hidePromoElements(animate);

    if (animate) {
      this.animateFilter(itemsToHide, itemsToShow);
    } else {
      // Update count immediately for non-animated filter
      this.updateProductCount(itemsToShow.length);
      // Instant filter (for initial page load)
      itemsToHide.forEach(item => {
        item.style.display = 'none';
        item.classList.add('collection-filter--hidden');
      });
      itemsToShow.forEach(item => {
        item.style.display = '';
        item.style.opacity = '1';
        item.classList.remove('collection-filter--hidden');
      });
    }
  }

  hidePromoElements(animate = true) {
    if (!this.promoElements) return;

    this.promoElements.forEach(el => {
      if (animate) {
        el.style.opacity = '0';
        setTimeout(() => {
          el.style.display = 'none';
          el.classList.add('collection-filter--hidden');
        }, this.animationDuration);
      } else {
        el.style.display = 'none';
        el.classList.add('collection-filter--hidden');
      }
    });
  }

  showPromoElements() {
    if (!this.promoElements) return;

    this.promoElements.forEach(el => {
      el.classList.remove('collection-filter--hidden');
      el.style.display = '';
      el.offsetHeight; // Trigger reflow
      el.style.opacity = '1';
    });
  }

  updateProductCount(count) {
    const countText = `${count} products`;

    if (this.productCountDesktop) {
      this.productCountDesktop.textContent = countText;
    }
    if (this.productCountMobile) {
      this.productCountMobile.textContent = countText;
    }
  }

  showAll() {
    if (!this.productItems) return;

    // Show promo elements again
    this.showPromoElements();

    // Restore original product count
    this.updateProductCount(this.originalCount);

    this.productItems.forEach(item => {
      item.classList.remove('collection-filter--hidden');
      item.classList.add('collection-filter--showing');
      item.style.display = '';

      // Trigger reflow for animation
      item.offsetHeight;
      item.style.opacity = '1';
    });

    setTimeout(() => {
      this.productItems.forEach(item => {
        item.classList.remove('collection-filter--showing');
      });
    }, this.animationDuration);
  }

  animateFilter(itemsToHide, itemsToShow) {
    // First, fade out items to hide
    itemsToHide.forEach(item => {
      item.classList.add('collection-filter--hiding');
      item.style.opacity = '0';
    });

    // After fade out, hide them and show the matching items
    setTimeout(() => {
      itemsToHide.forEach(item => {
        item.style.display = 'none';
        item.classList.remove('collection-filter--hiding');
        item.classList.add('collection-filter--hidden');
      });

      // Update product count when items switch
      this.updateProductCount(itemsToShow.length);

      // Show matching items
      itemsToShow.forEach(item => {
        item.classList.remove('collection-filter--hidden');
        item.style.display = '';
        // Trigger reflow
        item.offsetHeight;
        item.classList.add('collection-filter--showing');
        item.style.opacity = '1';
      });

      setTimeout(() => {
        itemsToShow.forEach(item => {
          item.classList.remove('collection-filter--showing');
        });
      }, this.animationDuration);

    }, this.animationDuration);
  }

  updateUrl(filterHandle) {
    const url = new URL(window.location.href);

    if (filterHandle) {
      url.searchParams.set('filter', filterHandle);
    } else {
      url.searchParams.delete('filter');
    }

    // Update URL without page reload
    window.history.pushState({ filter: filterHandle }, '', url.toString());
  }
}

// Handle browser back/forward
window.addEventListener('popstate', (e) => {
  if (e.state && e.state.filter !== undefined) {
    const filter = new CollectionFilter();
    if (e.state.filter) {
      filter.applyFilter(e.state.filter, true);
      filter.updateActiveState(e.state.filter);
    } else {
      filter.showAll();
      filter.updateActiveState('');
    }
  }
});

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new CollectionFilter();
});
